import { useCallback, useEffect, useState } from "react";
import { FileText, Download, RefreshCw } from "lucide-react";
import { Modal, MoneyInput } from "../lib/ui";
import { api, download, localToday, money } from "../lib/api";

const DENOMS = [200, 100, 50, 20, 10, 5, 1, 0.5, 0.25];

function BanknoteCounter({ counts, setCounts }) {
  const total = DENOMS.reduce((t, d) => t + d * (Number(counts[d]) || 0), 0);
  return (
    <div className="note-counter" data-testid="banknote-counter">
      {DENOMS.map((d) => (
        <label key={d} className="note-cell">
          <span>{d >= 1 ? `${d} TL` : `${d * 100} Kr`}</span>
          <input type="number" min="0" step="1" inputMode="numeric" data-testid={`note-${String(d).replace(".", "_")}`}
                 value={counts[d] ?? ""} placeholder="0"
                 onChange={(e) => setCounts({ ...counts, [d]: e.target.value.replace(/\D/g, "") })} />
          <small>{money(d * (Number(counts[d]) || 0))}</small>
        </label>
      ))}
      <div className="note-total"><span>Sayılan toplam</span><strong data-testid="banknote-total">{money(total)}</strong></div>
    </div>
  );
}

function CashCheck({ cash, counted, setCounted, counts, setCounts }) {
  const [byNotes, setByNotes] = useState(false);
  useEffect(() => {
    if (byNotes) setCounted(String(DENOMS.reduce((t, d) => t + d * (Number(counts[d]) || 0), 0)));
  }, [byNotes, counts, setCounted]);
  if (!cash) return <p className="muted-note" data-testid="cash-check-missing">"KASA HESABI" adlı kasa bulunamadı.</p>;
  const diff = counted === "" || counted === null ? null : Number(counted) - cash.expected;
  const tone = diff === null ? "" : Math.abs(diff) < 0.01 ? "positive" : diff > 0 ? "warning" : "negative";
  return (
    <div className="cash-check" data-testid="cash-check">
      <div className="cash-check-head">
        <strong>Nakit Kasa Kontrolü · {cash.account}</strong>
        <label className="perm-inline" data-testid="banknote-toggle">
          <input type="checkbox" checked={byNotes} onChange={(e) => setByNotes(e.target.checked)} /> Banknot adetleriyle say
        </label>
      </div>
      {byNotes && <BanknoteCounter counts={counts} setCounts={setCounts} />}
      <div className="cash-check-grid">
        <div><span>Giriş</span><strong className="positive">{money(cash.in)}</strong></div>
        <div><span>Çıkış</span><strong className="negative">{money(cash.out)}</strong></div>
        <div><span>Günün net nakdi (olması gereken)</span><strong data-testid="cash-expected">{money(cash.expected)}</strong></div>
        {byNotes ? (
          <div><span>Sayılan nakit</span><strong data-testid="cash-counted">{money(Number(counted) || 0)}</strong></div>
        ) : (
          <label className="field"><span>Sayılan nakit</span>
            <MoneyInput testid="daily-report-counted" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="Sayılan tutar" />
          </label>
        )}
        <div className={tone}><span>Fazla - Eksik</span>
          <strong data-testid="cash-diff">{diff === null ? "—" : `${diff > 0 ? "+" : ""}${money(diff)}`}</strong>
          {diff !== null && <small>{Math.abs(diff) < 0.01 ? "Kasa tutuyor" : diff > 0 ? "Kasada fazla var" : "Kasada eksik var"}</small>}
        </div>
      </div>
    </div>
  );
}

function SectionTable({ s }) {
  return (
    <section className="dr-section" data-testid={`dr-section-${s.key}`}>
      <h3>{s.title} <small>{s.rows.length} kayıt</small></h3>
      <div className="dr-table-wrap">
        <table className="dr-table">
          <thead><tr>{s.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {s.rows.length === 0 && <tr><td colSpan={s.head.length} className="muted">Kayıt yok</td></tr>}
            {s.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
            {s.total !== null && s.total !== undefined && (
              <tr className="dr-total"><td colSpan={s.total_col}>GENEL TOPLAM</td><td>{money(s.total)}</td>
                {s.head.length - s.total_col - 1 > 0 && <td colSpan={s.head.length - s.total_col - 1} />}</tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function DailyReportModal({ onClose }) {
  const [date, setDate] = useState(localToday());
  const [counted, setCounted] = useState("");
  const [counts, setCounts] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData((await api.get("/reports/daily/data", { params: { date } })).data); }
    catch (e) { setError(e?.response?.data?.detail || "Rapor yüklenemedi"); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const pdf = () => {
    const q = counted !== "" && counted !== null ? `&counted=${Number(counted)}` : "";
    const notes = DENOMS.filter((d) => Number(counts[d]) > 0).map((d) => `${d}:${Number(counts[d])}`).join(",");
    download(`/reports/daily?date=${date}${q}${notes ? `&notes=${notes}` : ""}`, `Gunluk-Rapor-${date}.pdf`).catch(() => setError("PDF alınamadı"));
  };
  const sm = data?.summary;
  return (
    <Modal title="Günlük Rapor · Önizleme" subtitle={data?.date_label || "Yükleniyor..."} size="xl" onClose={onClose} testid="daily-report-modal">
      <div className="dr-toolbar">
        <input type="date" data-testid="daily-report-date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value || localToday())} />
        <button type="button" className="secondary-button" onClick={load} disabled={loading} data-testid="daily-report-refresh">
          <RefreshCw size={14} /> {loading ? "Yükleniyor..." : "Yenile"}
        </button>
        <button type="button" className="primary-button" onClick={pdf} disabled={!data} data-testid="daily-report-download">
          <Download size={15} /> PDF indir
        </button>
      </div>
      {error && <div className="form-error" data-testid="daily-report-error">{error}</div>}
      {data && (
        <>
          <CashCheck cash={data.cash} counted={counted} setCounted={setCounted} counts={counts} setCounts={setCounts} />
          <div className="dr-summary" data-testid="dr-summary">
            <div><span>Ciro</span><strong>{money(sm.ciro)}</strong></div>
            <div><span>Masraf</span><strong>{money(sm.masraf)}</strong></div>
            <div><span>Ciro − Masraf</span><strong>{money(sm.toplam)}</strong></div>
            <div><span>Tahsilat</span><strong>{money(sm.tahsilat)}</strong></div>
            <div><span>Ödeme</span><strong>{money(sm.odeme)}</strong></div>
          </div>
          {data.sections.map((s) => <SectionTable key={s.key} s={s} />)}
        </>
      )}
    </Modal>
  );
}

export function DailyReportButton({ className = "secondary-button" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} data-testid="daily-report-button" onClick={() => setOpen(true)}>
        <FileText size={15} /> Günlük rapor
      </button>
      {open && <DailyReportModal onClose={() => setOpen(false)} />}
    </>
  );
}
